import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ProviderActivityItem } from '@raptor/contracts';
import { apiClient } from '../lib/api-client.js';

const channels = [
  { sourceKind: 'WEBSITE', label: 'Website', detail: 'First-party sessions, product activity, carts, checkout, and orders.' },
  { sourceKind: 'WOOCOMMERCE', label: 'WooCommerce', detail: 'Customer, cart, order, cancellation, and refund references.' },
  { sourceKind: 'BCONNECT', label: 'B-Connect', detail: 'Catalog files continue through validation, reconciliation, and approval.' },
  { sourceKind: 'WHATSAPP', label: 'WhatsApp', detail: 'Inbound and outbound-compatible conversation activity.' },
  { sourceKind: 'INSTAGRAM', label: 'Instagram', detail: 'Referral, campaign, and conversation-source activity.' },
  { sourceKind: 'FACEBOOK', label: 'Facebook', detail: 'Referral, campaign, and conversation-source activity.' },
] as const;

const domainLabel = (value: ProviderActivityItem['domainRecordType']): string => {
  switch (value) {
    case 'JOURNEY_SESSION': return 'Customer journey session';
    case 'JOURNEY_EVENT': return 'Customer journey event';
    case 'CONVERSATION_EVENT': return 'Conversation event';
    case 'CATALOG_IMPORT': return 'Catalog import';
    default: return 'Operational event';
  }
};

export function IntegrationsWorkspace({ organizationId }: { organizationId: string }) {
  const [activity, setActivity] = useState<ProviderActivityItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try { setActivity(await apiClient.getProviderActivity(organizationId)); setError(null); }
    catch { setActivity([]); setError('Channel activity is temporarily unavailable.'); }
  }, [organizationId]);
  useEffect(() => { void load(); }, [load]);
  const counts = useMemo(() => {
    const next = new Map<string, number>();
    for (const item of activity) next.set(item.sourceKind, (next.get(item.sourceKind) ?? 0) + 1);
    return next;
  }, [activity]);

  return <section className="workspace-rail" aria-labelledby="integrations-title">
    <div className="rail-heading">
      <div><p className="eyebrow">Integrations</p><h2 id="integrations-title">Channel activity</h2></div>
      <button className="text-action" type="button" onClick={() => void load()}>Refresh</button>
    </div>
    <p className="context-card__meta">Supported channels share one validated intake boundary. External accounts and credentials are managed separately.</p>
    {error ? <p className="notice notice--error" role="alert">{error}</p> : null}
    <div className="dashboard-grid" aria-label="Supported channels">
      {channels.map((channel) => <article className="context-card" key={channel.sourceKind}>
        <span className="context-card__meta">Supported channel</span>
        <strong>{channel.label}</strong>
        <p>{channel.detail}</p>
        <span>{counts.get(channel.sourceKind) ?? 0} recent event{counts.get(channel.sourceKind) === 1 ? '' : 's'}</span>
      </article>)}
    </div>
    <div className="rail-heading"><div><p className="eyebrow">Operations</p><h3>Recent intake</h3></div></div>
    {activity.length ? <ol className="crm-list">{activity.map((item) => <li key={item.id}>
      <strong>{item.sourceKind.replaceAll('_', ' ')}</strong>
      <span>{item.eventType.replaceAll('_', ' ')}</span>
      <span>{domainLabel(item.domainRecordType)}</span>
      <span>{new Date(item.sourceOccurredAt).toLocaleString()}</span>
    </li>)}</ol> : !error ? <p>No channel activity has been received for this authorized workspace.</p> : null}
  </section>;
}
